<template>
  <v-container fluid px-0>
    <v-row>
      <v-col v-if="!readOnly" :cols="12">
        <bm-form-flow-layout
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
          row-align="center"
        >
          <bm-dropdown
            :value.sync="selectedNewItemValue"
            :label="$m.select + ' ' + itemNoun"
            :items="items"
            :item-text="itemText"
            :item-value="itemValue"
            :item-hidden="itemHidden"
            data-qa="quantity_grid_items"
          />

          <bm-number-q
            :value.sync="selectedNumberToAdd"
            :min="allowNegative ? undefined : 0"
            enforce-min-max
            :label="$m.grid_qty"
            :suffix="getUnitByItemValue(selectedNewItemValue)"
            data-qa="quantity_grid_quantity"
          />

          <bm-number-q
            v-if="itemQuantityAvailable !== null"
            :value="quantityAvailable"
            :label="$m.stock_available_quantity"
            read-only
          />

          <bm-btn
            btn-class="mb-2"
            button-type="primary"
            :disabled="!canAddItem"
            data-qa="quantity_grid_add"
            @click="addItem"
            >{{ $m.add }} {{ itemNoun }}</bm-btn
          >
        </bm-form-flow-layout>
      </v-col>

      <v-col :cols="12">
        <bm-grid
          :items="value"
          :grid-configuration="mergedGridConfiguration"
          :item-noun="itemNoun"
          :item-key="rowKeyFunc()"
          :allow-drag="!readOnly && allowDragDrop"
          :allow-drop="!readOnly && allowDragDrop"
          :allow-sort="allowSort"
          @drag-drop="dragDropItem"
        >
          <template #expand="row">
            <slot name="expand" :row="row" />
          </template>
        </bm-grid>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmGrid, {
  type GridDragDropEvent,
} from "@/components/controls/grid/BmGrid.vue";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import { BmGridConfiguration } from "@/components/controls/grid/BmGridData";
import GridConfigurationBuilder from "@/components/controls/grid/builder/GridConfigurationBuilder";
import DataCellNumberEdit from "@/components/controls/grid/renderers/editors/DataCellNumberEdit.vue";
import type { AddItemEvent } from "@/components/controls/quantitygrid/QuantityGridTypes";
import Guid from "@/app/types/common/guid";
import type Key from "@/app/types/common/key";
import messages, { keys } from "@/plugins/i18n";
import { computed, watch } from "vue";
import { useGridItemsDragDrop } from "@/components/composables/dragDropUtils";
import { makeRef } from "@/components/composables/reactivityTypes";

export interface BmQuantityGridProps {
  value: readonly any[];
  valueText?: VuetifyPropertyArg;
  valueUnits?: VuetifyPropertyArg;
  valueCount: string;
  valueSequence?: string | null;
  valueKey?: VuetifyPropertyArg;
  items?: readonly any[];
  itemText?: VuetifyPropertyArg;
  itemValue?: VuetifyPropertyArg;
  itemUnits?: VuetifyPropertyArg;
  itemNoun?: string;
  itemHidden?: (item: any) => boolean;
  itemQuantityAvailable?: ((itemValue: any) => number | null) | null;
  allowOrdering?: boolean;
  allowDragDrop?: boolean;
  allowNegative?: boolean;
  allowItemEdit?: boolean;
  editActionText?: Key;
  defaultSelectedItemValue?: any;
  readOnly?: boolean;
  gridConfiguration?: BmGridConfiguration;
  allowSort?: boolean;
}

const props = withDefaults(defineProps<BmQuantityGridProps>(), {
  valueText: () => "text",
  valueUnits: () => "",
  valueSequence: null,
  valueKey: () => "key",
  items: () => [],
  itemText: () => "text",
  itemValue: () => "value",
  itemUnits: () => "",
  itemNoun: () => "",
  itemQuantityAvailable: null,
  allowOrdering: false,
  allowDragDrop: false,
  allowNegative: false,
  allowItemEdit: false,
  editActionText: () => keys.edit,
  readOnly: false,
  gridConfiguration: () => new BmGridConfiguration(),
  allowSort: true,
});

export interface BmQuantityGridEvents {
  (event: "edit-item", value: any): void;
  (event: "update:value", value: readonly any[]): void;
  (event: "remove", value: any): void;
  (event: "add-item", value: AddItemEvent): void;
}
const emit = defineEmits<BmQuantityGridEvents>();

const selectedNewItemValue = makeRef<any | null>(null);
const selectedNumberToAdd = makeRef(1);

const canAddItem = computed(
  () => !!selectedNumberToAdd.value && !!selectedNewItemValue.value
);

const mergedGridConfiguration = computed(() => {
  const newConfiguration = Object.assign({}, props.gridConfiguration);
  const maker = new GridConfigurationBuilder<any>();

  if (Object.values(newConfiguration.columns).length == 0) {
    maker.addTextColumn(
      { id: "text", headingText: props.itemNoun },
      (
        row // title
      ) => getPropertyFromItem(row, props.valueText)
    );
  }

  let qtyColumn = maker
    .addNumberColumn(keys.grid_qty, (line) =>
      getPropertyFromItem(line, props.valueCount)
    )
    .withSuffixExtractor((line) => getPropertyFromItem(line, props.valueUnits));
  if (!props.readOnly) {
    qtyColumn.withEditDialog(
      DataCellNumberEdit,
      (item, val) => {
        item[props.valueCount] = val;
      },
      {
        min: props.allowNegative ? undefined : 0,
      }
    );
  }

  if (!props.readOnly) {
    const actions = maker.addActionsColumn();
    if (props.allowItemEdit) {
      actions.withActionByKey(props.editActionText, (item) => {
        emit("edit-item", item);
      });
    }

    if (props.allowOrdering) {
      actions.withAction(messages.grid_move_up, (row) => moveItem(row, true));
      actions.withAction(messages.grid_move_down, (row) =>
        moveItem(row, false)
      );
    }

    actions.withAction(messages.grid_remove, (item) => {
      let arrayClone = [...props.value];
      const index = arrayClone.indexOf(item);
      if (index > -1) {
        arrayClone.splice(index, 1);
        emit("update:value", arrayClone);
        emit("remove", item);
      }
    });
  }

  // concatenate the columns
  newConfiguration.columns = {
    ...newConfiguration.columns,
    ...maker.build().columns,
  };

  return newConfiguration;
});

const quantityAvailable = computed(() => {
  if (props.itemQuantityAvailable && selectedNewItemValue.value) {
    return props.itemQuantityAvailable(selectedNewItemValue.value);
  }
  return null;
});

const defaultSelectedItemValueWatcher = () => {
  selectedNewItemValue.value = props.defaultSelectedItemValue ?? null;
};

const getUnitByItemValue = (itemId: any) => {
  if (itemId) {
    const item = props.items.find((item) => {
      const itemValue = getPropertyFromItem(item, props.itemValue);
      return (
        itemValue === itemId ||
        // handle the Guid equals case
        (Guid.isGuid(itemValue.value) &&
          Guid.isGuid(itemId.value) &&
          itemValue.equals(itemId.value))
      );
    });

    return getPropertyFromItem(item, props.itemUnits);
  }

  return "";
};

const addItem = () => {
  const nextSequence = props.valueSequence
    ? props.value.reduce(
        (max, value) => Math.max(max, value[props.valueSequence!]),
        -1
      ) + 1
    : 0;

  emit("add-item", {
    itemValue: selectedNewItemValue.value,
    count: selectedNumberToAdd.value,
    nextSequence,
  });
};

const moveItem = (item: any, up: boolean) => {
  const idx = props.value.indexOf(item);
  let newOrder = props.value.slice();

  if (up && idx > 0) {
    newOrder[idx] = newOrder[idx - 1];
    newOrder[idx - 1] = item;
  } else if (!up && idx < props.value.length - 1) {
    newOrder[idx] = newOrder[idx + 1];
    newOrder[idx + 1] = item;
  }

  // rewrite sequences
  if (props.valueSequence === null) {
    throw new Error("valueSequence is required if ordering is enabled");
  }
  newOrder.forEach((value, idx) => (value[props.valueSequence!] = idx));

  emit("update:value", newOrder);
};

const rowKeyFunc = (): ((itemRow: any) => string) => {
  if (props.valueKey instanceof Function) {
    return props.valueKey;
  }
  return (itemRow: any) =>
    getPropertyFromItem(itemRow, props.valueKey, itemRow.key)?.toString();
};

const useGridItemsDragDrop2 = () => {
  return {
    ...useGridItemsDragDrop(
      computed(() => props.value),
      rowKeyFunc(),
      (updatedValues) => {
        updatedValues.forEach(
          (value, idx) => (value[props.valueSequence!] = idx)
        );

        emit("update:value", updatedValues);
      }
    ),
  };
};
const dragDropItem = (event: GridDragDropEvent) => {
  if (props.valueSequence === null) {
    throw new Error("valueSequence is required if ordering is enabled");
  }

  const { onDrop } = useGridItemsDragDrop2();
  onDrop(event);
};

watch(() => props.defaultSelectedItemValue, defaultSelectedItemValueWatcher, {
  immediate: true,
});
/**
 * New version of the quantity grid that allows for more customisation of the grid and the use of "real" objects as
 * values that do not need transforming.
 */
</script>
